import React, { useState } from 'react'
import axios, { AxiosError } from 'axios'
import { Formik } from 'formik'
import { Moment } from 'moment-timezone'
import isEqual from 'lodash/isEqual'
import Modal from '../shared/modal'
import SectionTitle from '../shared/sectionTitle'
import { portionTypes } from '../shared/common'
import { t } from '../../i18n'
import TenantDateTimePicker from '../shared/TenantDateTimePicker'
import OrderItemRow, { OrderItemSelection } from './OrderItemRow'
import { PortionType } from '../recipes/types'

interface OrderItemForm {
  recipe_id: number | null
  amount: number
  opted_out_product_ids: number[]
}

const BLANK_ORDER_ITEM: OrderItemForm = { recipe_id: null, amount: 1, opted_out_product_ids: [] }

interface OrderFormValues {
  portionType: PortionType
  paid: boolean
  orderItems: OrderItemForm[]
  time: Moment | null
}

interface Props {
  currency: { code: string }
  onClose: () => void
  onError: (message: string) => void
}

const OrderCreationForm = ({ currency, onClose, onError }: Props) => {
  const [selections, setSelections] = useState<(OrderItemSelection | null)[]>([null])

  const createOrder = ({ portionType, paid, orderItems, time }: OrderFormValues) => {
    axios.post(
      Routes.create_order_orders_path(), {
        portion_type: portionType,
        paid,
        order_items_attributes: orderItems,
        picked_time_slot: (portionType === 'batch' && time) ? time.toISOString() : undefined
      }
    ).then(
      () => window.location.reload()
    ).catch((error: AxiosError<{ error: string }>) => {
      onError(error.response?.data?.error ?? '')
    })
  }

  return (
    <Formik<OrderFormValues>
      initialValues={{
        portionType: 'bowl',
        paid: false,
        orderItems: [BLANK_ORDER_ITEM],
        time: null
      }}
      onSubmit={createOrder}
    >
      {({ values, handleSubmit, setFieldValue }) => {
        const untouched = isEqual(values.orderItems, [BLANK_ORDER_ITEM])

        const onPortionTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          if (!untouched && !confirm('Selected items will be lost')) return

          setFieldValue('portionType', e.target.value)
          setFieldValue('orderItems', [BLANK_ORDER_ITEM])
          setSelections([null])
        }

        const onSelectRecipe = (idx: number, selection: OrderItemSelection) => {
          const newOrderItems = [...values.orderItems]
          newOrderItems[idx] = { ...newOrderItems[idx], recipe_id: selection.version.id, opted_out_product_ids: [] }
          setFieldValue('orderItems', newOrderItems)

          const newSelections = [...selections]
          newSelections[idx] = selection
          setSelections(newSelections)
        }

        const onAmountChange = (idx: number, amount: number) => {
          const newOrderItems = [...values.orderItems]
          newOrderItems[idx] = { ...newOrderItems[idx], amount }
          setFieldValue('orderItems', newOrderItems)
        }

        const onOptedOutProductToggle = (idx: number, productId: number, included: boolean) => {
          const newOrderItems = [...values.orderItems]
          const current = newOrderItems[idx].opted_out_product_ids
          newOrderItems[idx] = {
            ...newOrderItems[idx],
            opted_out_product_ids: included ? current.filter(id => id !== productId) : [...current, productId]
          }
          setFieldValue('orderItems', newOrderItems)
        }

        const onAddOrderItem = () => {
          setFieldValue('orderItems', [...values.orderItems, BLANK_ORDER_ITEM])
          setSelections([...selections, null])
        }

        const onRemoveOrderItem = (idx: number) => {
          if (values.orderItems.length <= 1) return

          const newOrderItems = [...values.orderItems]
          newOrderItems.splice(idx, 1)
          setFieldValue('orderItems', newOrderItems)

          const newSelections = [...selections]
          newSelections.splice(idx, 1)
          setSelections(newSelections)
        }

        const orderTotal = values.orderItems.reduce(
          (sum, item, idx) => sum + item.amount * (selections[idx]?.version.price ?? 0), 0
        )

        const renderTimePicker = () => {
          if (values.portionType === 'bowl') return null

          return (
            <div className="mb-5">
              <div className='row'>
                <div className='col-sm-8'>
                  <div className='form-group mb-0'><label>Time</label></div>
                  <TenantDateTimePicker
                    className='order-time-picker'
                    label={" "}
                    value={values.time ?? null}
                    onChange={time => setFieldValue('time', time)}
                  />
                </div>
              </div>
            </div>
          )
        }

        return (
          <Modal
            onClose={onClose}
            headerContent={<SectionTitle rows={['New', 'Order']} paddingLeft={0} />}
            footerContent={(
              <div className='d-flex justify-content-between align-items-end flex-1'>
                <button onClick={() => handleSubmit()} className='btn btn-primary'>
                  Create Order
                </button>
                <SectionTitle rows={['Total:', `${orderTotal.toFixed(2)} ${currency.code}`]} className='text-end' />
              </div>
            )}
            widen={true}
          >
            <form autoComplete='off' onSubmit={handleSubmit} className='order-modal'>
              <div className='row'>
                <div className='col-sm-4'>
                  <div className='form-group mb-2'><label>Type</label></div>
                  <div className='ms-2'>
                    {portionTypes.map((portionType: string) =>
                      <label
                        key={portionType}
                        className={portionType === portionTypes[0] ? 'radio-bowl me-4' : 'radio-batch mb-4'}
                      >
                        <input
                          type='radio'
                          name='portionType'
                          value={portionType}
                          onChange={(e) => onPortionTypeChange(e)}
                          checked={values.portionType === portionType}
                        />
                        {t(`recipes.${portionType}`)}
                      </label>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className='col-sm-8'>
                  <div className='form-group mb-0'><label>Recipe</label></div>
                </div>
                <div className='col-sm-3'>
                  <div className='form-group mb-0'><label>Amount</label></div>
                </div>
                <div className='col-sm-1'></div>
              </div>
              {values.orderItems.map((orderItem, idx) => (
                <OrderItemRow
                  key={idx}
                  idx={idx}
                  portionType={values.portionType}
                  currency={currency}
                  amount={orderItem.amount}
                  optedOutProductIds={orderItem.opted_out_product_ids}
                  selection={selections[idx] ?? null}
                  removeDisabled={values.orderItems.length <= 1}
                  onSelect={onSelectRecipe}
                  onAmountChange={onAmountChange}
                  onOptedOutProductToggle={onOptedOutProductToggle}
                  onRemove={onRemoveOrderItem}
                />
              ))}
              <div className='mb-5'>
                <span
                  className='button-add pointer mb-5'
                  onClick={onAddOrderItem}
                >
                  <i className='fa fa-plus-circle large me-2' />
                  <span>Add</span>
                </span>
              </div>
              {renderTimePicker()}
              <label className='radio-paid me-4'>
                <input
                  type='radio'
                  name='paid'
                  onChange={() => setFieldValue('paid', true)}
                  checked={!!values.paid}
                />
                Paid
              </label>
              <label className='radio-unpaid'>
                <input
                  type='radio'
                  name='paid'
                  onChange={() => setFieldValue('paid', false)}
                  checked={!values.paid}
                />
                Unpaid
              </label>
            </form>
          </Modal>
        )
      }}
    </Formik>
  )
}

export default OrderCreationForm
